import BackButton from '@/components/ui/BackButton'
import { isReadOnly } from '@/lib/permissions'
import { formatDate, formatDateTime } from '@/lib/utils'
import { useFindLinkCandidateById, useKeepSeparateMutation, useLinkPatientsMutation } from '@/services/deduplication'
import { borderRadius, colors, fontSizes, fontWeights } from '@/theme'
import type { CandidatePatient, DuplicateCandidate } from '@/types/deduplication'
import { LinkOutlined } from '@mui/icons-material'
import { Box, Button, CircularProgress, Divider, Paper, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DeferDialog, DontLinkDialog, LinkConfirmDialog } from './sections/confirmation-dialogs'
import MedicationRequestsCardsRow from './sections/medication-requests-cards-row'
import { gridColumns } from './grid'

const cellSx = { px: 1.5, py: 1, borderRadius: borderRadius.sm } as const
const highlightedCellSx = { ...cellSx, bgcolor: colors.highlight.bg, border: `1px solid ${colors.highlight.border}` }
const defaultCellSx = { ...cellSx, bgcolor: colors.grey[50], border: `1px solid ${colors.border.default}` }

function getFieldValue(record: object, key: string): string {
  const val = (record as Record<string, unknown>)[key]
  if (val === null || val === undefined || val === '') return 'None'
  if (typeof val === 'boolean') return val ? 'True' : 'False'
  return String(val)
}

function formatAddress(address: CandidatePatient['address']): string {
  if (!address) return 'None'
  const parts = [address.street, address.city, address.postal_code, address.county, address.country].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : 'None'
}

function parseSafeguarding(value: string | null): { question: string; answer: string }[] | null {
  if (!value) return null
  try {
    const parsed = JSON.parse(value) as Record<string, string>
    const entries = Object.entries(parsed)
    if (entries.length === 0) return null
    return entries.map(([q, a]) => ({ question: q, answer: a }))
  } catch {
    return null
  }
}

function SafeguardingQAList({ items }: { items: { question: string; answer: string }[] }) {
  return (
    <Box component='ul' sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
      {items.map(({ question, answer }) => (
        <Box component='li' key={question} sx={{ py: 0.25 }}>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            {question} <strong>{answer}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

const PATIENT_FIELDS: { label: string; key?: string; getValue?: (p: CandidatePatient) => string }[] = [
  { label: 'First Name', key: 'first_name' },
  { label: 'Last Name', key: 'last_name' },
  { label: 'Gender', key: 'gender' },
  {
    label: 'Date of birth',
    getValue: (p: CandidatePatient) => (p.date_of_birth ? formatDate(p.date_of_birth) : 'None')
  },
  { label: 'Post code', key: 'postal_code' },
  { label: 'User email address', getValue: (p: CandidatePatient) => p.user?.email ?? 'None' }
]

const ADDITIONAL_FIELDS: { label: string; key?: string; getValue?: (p: CandidatePatient) => string }[] = [
  { label: 'ID verified', getValue: (p: CandidatePatient) => (p.id_verified ? 'True' : 'False') },
  { label: 'NHS number', getValue: (p: CandidatePatient) => p.nhs_number || 'None' },
  { label: 'Red flag', getValue: (p: CandidatePatient) => (p.red_flag ? 'True' : 'False') },
  { label: 'Red flag reason', getValue: (p: CandidatePatient) => p.red_flag_reason || 'None' },
  { label: 'GP name', getValue: (p: CandidatePatient) => p.gp_name || 'None' },
  {
    label: 'SCR updated at',
    getValue: (p: CandidatePatient) =>
      p.summary_care_record_updated_at ? formatDateTime(p.summary_care_record_updated_at) : 'None'
  },
  { label: 'Patient address', getValue: (p: CandidatePatient) => formatAddress(p.address) }
]

function ComparisonRow({
  label,
  fieldKey,
  getValue,
  patients
}: {
  label: string
  fieldKey?: string
  getValue?: (p: CandidatePatient) => string
  patients: CandidatePatient[]
}) {
  const values = getValue ? patients.map(p => getValue(p)) : patients.map(p => getFieldValue(p, fieldKey!))
  const isSame = values.every(v => v === values[0])

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(patients.length), alignItems: 'center' }}>
      <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, pl: 3, py: 1.5 }}>{label}</Typography>
      {patients.map((patient, idx) => {
        const isDiff = !isSame && values[idx] !== values[0]
        return (
          <Box key={patient.hash} sx={{ px: 1.5, py: 1.5 }}>
            <Box sx={isDiff ? highlightedCellSx : defaultCellSx}>
              <Typography sx={{ fontSize: fontSizes.base }}>{values[idx]}</Typography>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

function SafeguardingComparisonRow({ patients }: { patients: CandidatePatient[] }) {
  const parsed = patients.map(p => parseSafeguarding(p.safeguarding_questions))
  const hasData = parsed.map(p => p !== null)
  const isSame = parsed.every(p => JSON.stringify(p) === JSON.stringify(parsed[0]))

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(patients.length), alignItems: 'start' }}>
      <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, pl: 3, py: 1.5 }}>
        Safeguarding questions
      </Typography>
      {patients.map((patient, idx) => {
        const isDiff = !isSame && hasData[idx] !== hasData[0]
        return (
          <Box key={patient.hash} sx={{ px: 1.5, py: 1.5 }}>
            <Box sx={isDiff ? highlightedCellSx : defaultCellSx}>
              {parsed[idx] ? (
                <SafeguardingQAList items={parsed[idx]} />
              ) : (
                <Typography sx={{ fontSize: fontSizes.base }}>None</Typography>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default function LinkReviewPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { groupId } = useParams<{ groupId: string }>()
  const readOnly = isReadOnly()

  const linkMutation = useLinkPatientsMutation()
  const keepSeparateMutation = useKeepSeparateMutation()

  const navState = location.state as { candidate?: DuplicateCandidate; fallbackPath?: string } | null
  const candidateFromState = navState?.candidate
  const fallbackPath = navState?.fallbackPath ?? '/deduplication'

  const groupIdNum = groupId ? Number(groupId) : Number.NaN
  // Always look it up: the candidate arrives via `<Link state>`, which React Router keeps in
  // `window.history.state` across a reload — seeding from it pinned the page to a stale snapshot.
  const shouldLookup = Number.isFinite(groupIdNum)
  const { data: candidateFromQuery, isLoading: isLookupLoading } = useFindLinkCandidateById(
    shouldLookup ? groupIdNum : null,
    candidateFromState?.patient_a?.hash
  )

  const candidate: DuplicateCandidate | undefined = candidateFromQuery ?? candidateFromState ?? undefined
  const patients = candidate ? [candidate.patient_a, candidate.patient_b] : []
  const patientName = candidate
    ? `${candidate.patient_a.first_name ?? ''} ${candidate.patient_a.last_name ?? ''}`.trim()
    : ''

  const [dialogs, setDialogs] = useState({ link: false, dontLink: false, defer: false })

  const openDialog = useCallback((key: keyof typeof dialogs) => {
    setDialogs(d => ({ ...d, [key]: true }))
  }, [])

  const closeDialog = useCallback((key: keyof typeof dialogs) => {
    setDialogs(d => ({ ...d, [key]: false }))
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: this is fine
  const handleLink = useCallback(() => {
    if (isReadOnly()) return
    if (!candidate || patients.length < 2) return
    closeDialog('link')

    linkMutation.mutate(
      {
        linkHash: patients[1].hash,
        body: { basePatientHash: patients[0].hash }
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Patient records linked successfully.', { variant: 'success' })
          navigate(fallbackPath)
        }
      }
    )
  }, [candidate, patients])

  // biome-ignore lint/correctness/useExhaustiveDependencies: this is fine
  const handleDontLink = useCallback(() => {
    if (isReadOnly()) return
    if (!candidate || patients.length < 2) return
    closeDialog('dontLink')

    keepSeparateMutation.mutate(
      {
        hash: patients[0].hash,
        body: { basePatientHash: patients[1].hash, groupByUser: false }
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Patient records marked as separate.', { variant: 'success' })
          navigate(fallbackPath)
        }
      }
    )
  }, [candidate, patients])

  if (!candidate) {
    if (shouldLookup && isLookupLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )
    }
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Link recommendation not found. Please go back and select a candidate from the list.</Typography>
        <Button variant='link' onClick={() => navigate(fallbackPath)}>
          Back to duplicates
        </Button>
      </Box>
    )
  }

  const isMutating = linkMutation.isPending || keepSeparateMutation.isPending

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
      <BackButton fallbackPath={fallbackPath} defaultLabel='Back to duplicates' />

      <Typography variant='pageTitle' component='h1'>
        Review Patients To Link
      </Typography>

      <Paper variant='outlined' sx={{ borderRadius: borderRadius.md, overflow: 'hidden' }}>
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography sx={{ fontSize: fontSizes.lg, fontWeight: fontWeights.semibold }}>
              Link recommendation: {patientName} ({patients.length})
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(patients.length) }}>
          <Box />
          {patients.map((patient, idx) => (
            <Box key={patient.hash} sx={{ px: 1.5, py: 2 }}>
              <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 1 }}>
                {idx === 0 ? 'Patient 1 (primary patient)' : `Patient ${idx + 1}`}
              </Typography>
              <Box sx={{ p: 2, borderRadius: borderRadius.sm, border: `1px solid ${colors.border.default}` }}>
                <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>
                  {patient.user?.display_name ?? `${patient.first_name} ${patient.last_name}`}
                </Typography>
                <Typography sx={{ fontSize: fontSizes.sm }}>
                  Email address: <strong>{patient.user?.email ?? 'N/A'}</strong>
                </Typography>
                <Typography sx={{ fontSize: fontSizes.sm }}>
                  Customer ID: <strong>{patient.user?.hash ?? 'N/A'}</strong>
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(patients.length) }}>
          <Typography
            sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, color: colors.brand.primary, pl: 3, py: 1.5 }}
          >
            Patient details
          </Typography>
        </Box>

        {PATIENT_FIELDS.map(field => (
          <ComparisonRow
            key={field.label}
            label={field.label}
            fieldKey={field.key}
            getValue={field.getValue}
            patients={patients}
          />
        ))}

        <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns(patients.length), mt: 1 }}>
          <Box sx={{ pl: 3, py: 1.5 }}>
            <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold }}>Additional Details</Typography>
            <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>
              These details may differ between the two records
            </Typography>
          </Box>
        </Box>

        {ADDITIONAL_FIELDS.map(field => (
          <ComparisonRow
            key={field.label}
            label={field.label}
            fieldKey={field.key}
            getValue={field.getValue}
            patients={patients}
          />
        ))}

        {patients.some(p => p.id_document_provided && p.id_verified) && (
          <ComparisonRow
            label='ID document provided'
            getValue={(p: CandidatePatient) => p.id_document_provided ?? 'None'}
            patients={patients}
          />
        )}

        <SafeguardingComparisonRow patients={patients} />

        <MedicationRequestsCardsRow patients={patients} backLabel='Back to link review' />
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1 }}>
        {/* <Button
          variant='link'
          startIcon={<Reply sx={{ transform: 'scaleX(-1)' }} />}
          onClick={() => openDialog('defer')}
        >
          Defer
        </Button> */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='secondary'
            onClick={() => openDialog('dontLink')}
            disabled={isMutating || readOnly}
            sx={{ px: 4 }}
          >
            Don&apos;t link patients
          </Button>
          <Button
            variant='primary'
            startIcon={<LinkOutlined />}
            onClick={() => openDialog('link')}
            disabled={isMutating || readOnly}
            sx={{ px: 4 }}
          >
            Link patients
          </Button>
        </Box>
      </Box>

      <LinkConfirmDialog open={dialogs.link} onConfirm={handleLink} onCancel={() => closeDialog('link')} />
      <DontLinkDialog open={dialogs.dontLink} onConfirm={handleDontLink} onCancel={() => closeDialog('dontLink')} />
      <DeferDialog
        open={dialogs.defer}
        onConfirm={() => {
          closeDialog('defer')
          navigate(fallbackPath)
        }}
        onCancel={() => closeDialog('defer')}
      />
    </Box>
  )
}
